import React, {useEffect, useMemo, useState} from 'react';
import {IonContent, IonModal, IonPage} from '@ionic/react';
import {useHistory} from 'react-router-dom';
import {config} from '../Constants'
import {ApiService} from "../api/ApiService";
import {VideoInfo} from "../api/model/VideosList";
import {DocumentInfo} from "../api/model/DocumentsList";
import LoadDetailHeader, { useTransparentHeaderContrast } from '../components/LoadDetailHeader';
import { ChevronLeft, ChevronRight, FolderDocIcon, MaintenanceIcon } from '../components/icons';
import { useLogPageVisit } from '../utils/logPageVisit';
import { logUserAction } from '../utils/logUserAction';

const MaintenanceVids: React.FC = () => {
    useLogPageVisit('LP Maintenance Videos', 'User viewed the Maintenance Videos page');
    const history = useHistory();
    const { onSheet, onIonScroll } = useTransparentHeaderContrast();
    const [mounted, setMounted] = useState(false);
    const [loadingDocs, setLoadingDocs] = useState(true);
    const [loadingVideos, setLoadingVideos] = useState(true);

    const [showModal, setShowModal] = useState(false);
    const [showModalDoc, setShowModalDoc] = useState(false);
    const [vidLink, setVidLink] = useState("");
    const [docs, setDocList] = useState<DocumentInfo[]>([]);
    const [doc, setDoc] = useState<DocumentInfo>();
    const [vidIds, setVidIds] = useState<VideoInfo[]>([]);

    useEffect(() => {
      const id = requestAnimationFrame(() => {
        setTimeout(() => setMounted(true), 30);
      });
      return () => cancelAnimationFrame(id);
    }, []);

    useEffect(() => {
        ApiService.getDocsList('maintenance', {isLp: 1, type: 'maintenance'}).then(it => setDocList(it)).finally(() => setLoadingDocs(false));
        ApiService.getVideosList('maintenance', {isLp: 1, type: 'maintenance'}).then(it => setVidIds(it)).finally(() => setLoadingVideos(false));
    }, []);

    function openDoc(nextDoc: DocumentInfo) {
        logUserAction('LP Maint Doc View', `User viewed a maintenance document: ${nextDoc.title}`);
        setDoc(nextDoc);
        if (config.isPlatformSci) {
            setShowModalDoc(true);
        }
    }

    function docHref(d?: DocumentInfo) {
        return 'https://westernexpressmobile.com/docs/' + d?.type + '_' + d?.id + '.' + d?.extension + "#toolbar=0";
    }

    const goBack = () => {
      if (history.length > 1) history.goBack();
      else history.replace('/docs');
    };

    const activeVideos = useMemo(() => vidIds.filter(v => v.active === 1), [vidIds]);
    const loading = loadingDocs || loadingVideos;
    const metaLine = loading
      ? 'Loading…'
      : `${docs.length} document${docs.length === 1 ? '' : 's'} · ${activeVideos.length} video${activeVideos.length === 1 ? '' : 's'}`;

    return (
        <IonPage>
            <LoadDetailHeader title="Maintenance" onBack={goBack} onSheet={onSheet} />

            <IonContent
              fullscreen
              className="design-a-content design-a-content--light"
              scrollEvents={true}
              onIonScroll={onIonScroll}
            >
                <div className={`design-a design-a--detail${mounted ? ' design-a--mounted' : ''}`}>
                    <div className="home-hero safety-hero">
                        <div className="safety-hero__badge" aria-hidden="true">
                            <MaintenanceIcon color="#ffffff" size={28} />
                        </div>
                        <div className="safety-hero__eyebrow">Maintenance resources</div>
                        <div className="safety-hero__title home-display">Maintenance</div>
                        <div className="safety-hero__meta">{metaLine}</div>
                    </div>

                    <div className="home-sheet safety-sheet">
                        <section className="safety-section">
                            <h2 className="safety-section__title home-section-label">Documents</h2>
                            {docs.length === 0 && !loadingDocs ? (
                                <div className="scoreboard-empty">
                                    <strong>No documents</strong>
                                    <p>Maintenance documents will appear here when available.</p>
                                </div>
                            ) : null}
                            <div className="safety-doc-list">
                                {docs.map((item, index) =>
                                    config.isPlatformSci ? (
                                        <button
                                            key={item.id}
                                            type="button"
                                            className="safety-doc-row"
                                            style={{ ['--safety-delay' as string]: `${0.35 + index * 0.04}s` }}
                                            onClick={() => openDoc(item)}
                                        >
                                            <span className="safety-doc-row__icon" aria-hidden="true">
                                                <FolderDocIcon size={18} />
                                            </span>
                                            <span className="safety-doc-row__label">{item.title}</span>
                                            <ChevronRight color="oklch(0.55 0.01 260)" />
                                        </button>
                                    ) : (
                                        <a
                                            key={item.id}
                                            href={docHref(item)}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="safety-doc-row"
                                            style={{ ['--safety-delay' as string]: `${0.35 + index * 0.04}s` }}
                                            onClick={() => setDoc(item)}
                                        >
                                            <span className="safety-doc-row__icon" aria-hidden="true">
                                                <FolderDocIcon size={18} />
                                            </span>
                                            <span className="safety-doc-row__label">{item.title}</span>
                                            <ChevronRight color="oklch(0.55 0.01 260)" />
                                        </a>
                                    )
                                )}
                            </div>
                        </section>

                        <section className="safety-section">
                            <h2 className="safety-section__title home-section-label">Videos</h2>
                            {activeVideos.length === 0 && !loadingVideos ? (
                                <div className="scoreboard-empty">
                                    <strong>No videos</strong>
                                    <p>Maintenance videos will appear here when available.</p>
                                </div>
                            ) : null}
                            <div className="safety-video-grid">
                                {activeVideos.map((video, index) => (
                                    <button
                                        key={video.id}
                                        type="button"
                                        className="safety-video-card"
                                        style={{ ['--safety-delay' as string]: `${0.45 + index * 0.04}s` }}
                                        onClick={() => { logUserAction('LP Maint Vid Play', `User played a maintenance video: ${video.title}`); setShowModal(true); setVidLink('https://www.youtube.com/embed/' + video.youtube); }}
                                    >
                                        <span className="safety-video-card__thumb-wrap">
                                            <img
                                                className="safety-video-card__thumb"
                                                alt=""
                                                src={`https://img.youtube.com/vi/${video.youtube}/0.jpg`}
                                            />
                                            <span className="safety-video-card__play" aria-hidden="true" />
                                        </span>
                                        <span className="safety-video-card__title">{video.title}</span>
                                    </button>
                                ))}
                            </div>
                        </section>
                    </div>
                </div>
            </IonContent>

            <IonModal isOpen={showModalDoc} cssClass="safety-viewer-modal" onDidDismiss={() => setShowModalDoc(false)}>
                <div className="safety-media-modal">
                    <div className="safety-media-modal__header">
                        <button type="button" className="safety-media-modal__back" onClick={() => setShowModalDoc(false)}>
                            <ChevronLeft size={20} />
                            Back
                        </button>
                        {doc?.title ? <div className="safety-media-modal__title">{doc.title}</div> : null}
                    </div>
                    <div className="safety-media-modal__body safety-media-modal__body--doc">
                        <iframe src={docHref(doc)} style={{ height: '100%', width: '100%', border: 'none' }} title="docs" allowFullScreen/>
                    </div>
                </div>
            </IonModal>

            <IonModal isOpen={showModal} cssClass="safety-video-modal" onDidDismiss={() => { setShowModal(false); setVidLink(''); }}>
                <div className="safety-media-modal">
                    <div className="safety-media-modal__header">
                        <button type="button" className="safety-media-modal__back" onClick={() => { setShowModal(false); setVidLink(''); }}>
                            <ChevronLeft size={20} />
                            Back
                        </button>
                    </div>
                    <div className="safety-media-modal__body safety-media-modal__body--video">
                        {vidLink ? (
                            <iframe id="video-modal" className="safety-media-modal__iframe" title="maintenance video" src={vidLink} allowFullScreen/>
                        ) : null}
                    </div>
                </div>
            </IonModal>
        </IonPage>
    );
};

export default MaintenanceVids;
